import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleBySlug, getRelatedArticles, articleCategories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  const relatedArticles = getRelatedArticles(slug || '', 3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-foreground-600 mb-4">该文章可能已被移除或链接无效</p>
            <Link to="/" className="inline-flex px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryLabel = articleCategories.find((c) => c.slug === article.category)?.name || article.category;

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '私密视频',
    },
    inLanguage: 'zh-CN',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.simishipin.com/article/${slug}`,
    },
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />

      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryLabel },
            { label: article.title },
          ]}
        />

        <article className="px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <header className="mb-6 md:mb-8">
              <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-5 text-sm text-foreground-600">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full whitespace-nowrap">
                  {categoryLabel}
                </span>
              </div>
            </header>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 私密视频`}
                className="w-full aspect-[2/1] object-cover object-top"
              />
            </div>

            <div
              className="prose prose-stone max-w-none mb-10 md:mb-14 article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <style>{`
              .article-content h2 {
                font-size: 1.5rem;
                font-weight: 700;
                margin-top: 2rem;
                margin-bottom: 0.75rem;
                color: oklch(var(--foreground-900));
              }
              .article-content h3 {
                font-size: 1.2rem;
                font-weight: 600;
                margin-top: 1.5rem;
                margin-bottom: 0.5rem;
                color: oklch(var(--foreground-800));
              }
              .article-content p {
                margin-bottom: 1rem;
                line-height: 1.8;
                color: oklch(var(--foreground-700));
              }
              .article-content strong {
                color: oklch(var(--foreground-900));
              }
            `}</style>

            <div className="border-t border-background-200 pt-6">
              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600">
                <span className="font-medium text-foreground-900">标签：</span>
                {article.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 rounded-full text-xs whitespace-nowrap">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="space-y-3 md:space-y-4">
                {relatedArticles.map((related) => (
                  <article key={related.slug} className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-accent-200 transition-all duration-300 flex flex-col sm:flex-row">
                    <Link to={`/article/${related.slug}`} className="block sm:w-48 shrink-0 aspect-video sm:aspect-auto overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        title={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </Link>
                    <div className="p-4 flex flex-col justify-between flex-1">
                      <div>
                        <Link to={`/article/${related.slug}`} className="block">
                          <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-accent-600 transition-colors duration-200 mb-2">
                            {related.title}
                          </h3>
                        </Link>
                        <p className="text-xs md:text-sm text-foreground-600 line-clamp-2">{related.excerpt}</p>
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                        <span><i className="ri-calendar-line mr-1"></i>{related.date}</span>
                        <span><i className="ri-time-line mr-1"></i>{related.readTime}</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}